import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

type SerializeResult = string | null | undefined

type UseUrlListStateOptions<T extends Record<string, unknown>> = {
  defaults: T
  parse: { [K in keyof T]: (raw: string | null, defaults: T) => T[K] }
  serialize: { [K in keyof T]: (value: T[K], defaults: T) => SerializeResult }
  /** Override URL param names (defaults to the state key) */
  keys?: Partial<Record<keyof T, string>>
}

/**
 * Sync list filters with the URL search string.
 * Values are available on the first render (no restore effect / double fetch).
 * Default values are omitted from the URL for clean shareable links.
 * Unrelated query params on the same page are preserved.
 */
export function useUrlListState<T extends Record<string, unknown>>({
  defaults,
  parse,
  serialize,
  keys,
}: UseUrlListStateOptions<T>): {
  values: T
  setValues: (patch: Partial<T>) => void
} {
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultsRef = useRef(defaults)
  const parseRef = useRef(parse)
  const serializeRef = useRef(serialize)
  const keysRef = useRef(keys)
  defaultsRef.current = defaults
  parseRef.current = parse
  serializeRef.current = serialize
  keysRef.current = keys

  const paramName = useCallback((k: keyof T) => {
    return (keysRef.current?.[k] as string | undefined) ?? (k as string)
  }, [])

  const searchKey = searchParams.toString()

  const values = useMemo(() => {
    const d = defaultsRef.current
    const p = parseRef.current
    const sp = new URLSearchParams(searchKey)
    const next = { ...d }
    for (const k of Object.keys(d) as (keyof T)[]) {
      next[k] = p[k](sp.get(paramName(k)), d)
    }
    return next
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchKey captures param changes; parsers via refs
  }, [searchKey, paramName])

  const setValues = useCallback(
    (patch: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const d = defaultsRef.current
          const p = parseRef.current
          const s = serializeRef.current
          const current = { ...d }
          for (const k of Object.keys(d) as (keyof T)[]) {
            current[k] = p[k](prev.get(paramName(k)), d)
          }
          const merged = { ...current, ...patch }
          const next = new URLSearchParams(prev)
          for (const k of Object.keys(d) as (keyof T)[]) {
            const name = paramName(k)
            next.delete(name)
            const serialized = s[k](merged[k], d)
            if (serialized != null && serialized !== '') {
              next.set(name, serialized)
            }
          }
          return next
        },
        { replace: true },
      )
    },
    [paramName, setSearchParams],
  )

  return { values, setValues }
}

/** Local calendar date as YYYY-MM-DD. */
export function formatYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Parse YYYY-MM-DD into a local Date, or null if invalid. */
export function parseYmd(raw: string | null): Date | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null
  const d = new Date(`${raw}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

export function parseEnumParam<T extends string>(
  raw: string | null,
  allowed: readonly T[],
  fallback: T,
): T {
  if (raw != null && (allowed as readonly string[]).includes(raw)) {
    return raw as T
  }
  return fallback
}

/** Omit empty / default strings from the URL. */
export function serializeOmitDefault(
  value: string,
  defaultValue: string,
): string | null {
  const trimmed = value.trim()
  if (!trimmed || trimmed === defaultValue) return null
  return trimmed
}
